import { useQueryClient } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import { useState } from 'react';
import { useEventCallback } from 'usehooks-ts';

import { trackDownloadAppButtonClicked } from '@/features/analytics/track/trackDownloadAppButtonClicked';
import { DownloadAppPlayNowModal } from '@/features/download/modals/downloadAppPlayNowModal';
import { useGetDuoPreferences } from '@/features/duos/hooks/useGetDuoPreferences';
import { useUpdateDuoPreferences } from '@/features/duos/hooks/useUpdateDuoPreferences';
import { PlayNowNotificationsModal } from '@/features/notifications/modals/playNowNotificationsModal';
import { requestFreshFetch } from '@/features/recommend/atoms/freshFetchRequested';
import { getRecommendedPlayersKey } from '@/features/recommend/hooks/useGetRecommendedPlayers';
import { Button } from '@/shared/components/ui/button';
import { Switch } from '@/shared/components/ui/switch';
import { cn } from '@/shared/utils/cn';
import { isNativePlatform } from '@/shared/utils/platform';

import { PlayNowModal } from './playNowModal';
import { useIsPlayNowToggleEnabled } from './useIsPlayNowToggleEnabled';

export interface PageHeaderPlayNowToggleProps {}

export const PageHeaderPlayNowToggle = () => {
  const [openModal, setOpenModal] = useState(false);
  const [isDownloadAppModalOpen, setIsDownloadAppModalOpen] = useState(false);
  const { data: readyToPlay, isLoading } = useGetDuoPreferences({
    options: { select: data => data.readyToPlay },
  });
  const isToggleEnabled = useIsPlayNowToggleEnabled();
  const isNative = isNativePlatform();

  const { mutate } = useUpdateDuoPreferences();
  const queryClient = useQueryClient();

  const handleOpenModal = useEventCallback(() => {
    setOpenModal(true);
  });

  const handleNotificationSuccess = useEventCallback(() => {
    mutate(
      { readyToPlay: true },
      {
        onSuccess: () => {
          requestFreshFetch();
          void queryClient.invalidateQueries({
            queryKey: getRecommendedPlayersKey(),
          });
        },
      },
    );
    handleOpenModal();
  });

  const handleToggle = useEventCallback(() => {
    if (!isToggleEnabled) {
      if (!isNative) {
        trackDownloadAppButtonClicked({ source: 'play_now_limit_modal' });
      }
      setIsDownloadAppModalOpen(true);
      return;
    }

    mutate(
      { readyToPlay: !readyToPlay },
      {
        onSuccess: () => {
          // Signal that the next fetch should replace cards entirely (not merge)
          requestFreshFetch();
          void queryClient.invalidateQueries({
            queryKey: getRecommendedPlayersKey(),
          });
        },
      },
    );
    if (!readyToPlay) handleOpenModal();
  });

  /**
   * Better not to render anything if the data is loading, then it will cause a flash of the wrong state.
   */
  if (isLoading) return null;

  return (
    <>
      <Button
        asChild
        variant="ghost"
        className={cn('relative border-0 bg-transparent p-0', {
          'before:bg-positive-600 before:animate-ping-subtle before:pointer-events-none before:absolute before:inset-0 before:rounded-lg':
            readyToPlay,
        })}
      >
        <label>
          <div
            className={cn(
              'relative z-10 flex h-full items-center gap-1.5 rounded-lg border-2 p-2',
              {
                'bg-positive-950 border-positive-600 inset-shadow-sm inset-shadow-white/70':
                  readyToPlay,
                'border-cold-600 bg-white/10': !readyToPlay,
              },
            )}
          >
            <Play
              className={cn('aspect-square size-4', {
                'text-positive-600': readyToPlay,
                'text-foreground': !readyToPlay,
              })}
            />
            <span className="font-nunito text-secondary-foreground text-xs leading-4 font-medium">
              Play now
            </span>
            <Switch
              className="data-[state=checked]:bg-positive-700 cursor-pointer"
              checked={readyToPlay}
              onCheckedChange={handleToggle}
            />
          </div>
        </label>
      </Button>
      <PlayNowModal openModal={openModal} setOpenModal={setOpenModal} />
      {isNative ? (
        <PlayNowNotificationsModal
          open={isDownloadAppModalOpen}
          onOpenChange={setIsDownloadAppModalOpen}
          onComplete={handleNotificationSuccess}
        />
      ) : (
        <DownloadAppPlayNowModal
          open={isDownloadAppModalOpen}
          onOpenChange={setIsDownloadAppModalOpen}
        />
      )}
    </>
  );
};
